import {useCallback, useEffect, useRef, useState} from 'react'
import {getExpertsPaginated, PublicExpert} from '../../services/expertsService'

// Infinite scroll para /expertise/search/experts/ cuando NO hay filtros de busqueda
// (modo "discovery" viniendo de "See all results" con scope Expert). El backend
// usa LimitOffsetPagination (envelope {count, next, previous, results}), asi que
// pedimos `limit=pageSize` y vamos moviendo `offset` hasta que `next` es null.
// Espejo de useInfiniteMembersSearch — mismo contrato para mantener la UX
// consistente entre /members-search y /expert-search.

export interface UseInfiniteExpertsSearchResult {
  items: PublicExpert[]
  loading: boolean
  hasMore: boolean
  error: Error | null
  loadMore: () => void
  /** Ref para el elemento sentinel que dispara la carga cuando entra al viewport. */
  sentinelRef: (node: HTMLElement | null) => void
  reset: () => void
}

interface Options {
  /** Si false, el hook no dispara ninguna carga. Se usa para saltar el modo infinite cuando hay query. */
  enabled?: boolean
  /** Cuantos items pedir por pagina. Default 20 — igual que members. */
  pageSize?: number
  /**
   * Distancia (px) desde el borde inferior del viewport a la que empieza a cargar
   * la siguiente pagina. Un margen positivo evita que el usuario vea el spinner
   * al llegar al final; anticipa la carga.
   */
  rootMargin?: string
}

export const useInfiniteExpertsSearch = ({
  enabled = true,
  pageSize = 20,
  rootMargin = '400px',
}: Options = {}): UseInfiniteExpertsSearchResult => {
  const [items, setItems] = useState<PublicExpert[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Guardar en refs para leer valores actualizados dentro de callbacks sin
  // recrear observers cada render.
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const offsetRef = useRef(0)
  const enabledRef = useRef(enabled)

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])
  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const reset = useCallback(() => {
    setItems([])
    setLoading(false)
    setHasMore(true)
    setError(null)
    offsetRef.current = 0
    loadingRef.current = false
    hasMoreRef.current = true
  }, [])

  const loadMore = useCallback(async () => {
    if (!enabledRef.current) return
    if (loadingRef.current || !hasMoreRef.current) return

    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const {results, next} = await getExpertsPaginated({
        limit: pageSize,
        offset: offsetRef.current,
      })
      const nextHasMore = Boolean(next) && results.length > 0

      setItems((prev) => {
        // Dedupe por id: en carreras de peticiones puede colarse un item repetido.
        const seen = new Set(prev.map((e) => String(e?.id)))
        const merged = [...prev]
        for (const e of results) {
          const id = String(e?.id ?? '')
          if (id && !seen.has(id)) {
            seen.add(id)
            merged.push(e)
          }
        }
        return merged
      })
      offsetRef.current += results.length
      hasMoreRef.current = nextHasMore
      setHasMore(nextHasMore)
    } catch (err) {
      setError(err as Error)
      hasMoreRef.current = false
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [pageSize])

  // Reset y primera carga cuando `enabled` cambia.
  useEffect(() => {
    if (!enabled) return
    reset()
    // Kick off inmediato (el sentinel puede tardar un frame en montarse).
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  // IntersectionObserver bindeado a un ref-callback. Usar ref-callback (en vez de
  // useRef + useEffect) permite reconectar el observer si el nodo cambia (por
  // ejemplo cuando el sentinel se re-monta al cambiar de query -> sin query).
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node || !enabled) return
      if (typeof IntersectionObserver === 'undefined') return

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const first = entries[0]
          if (first?.isIntersecting) loadMore()
        },
        {rootMargin}
      )
      observerRef.current.observe(node)
    },
    [enabled, loadMore, rootMargin]
  )

  // Cleanup al desmontar.
  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    []
  )

  return {items, loading, hasMore, error, loadMore, sentinelRef, reset}
}
